import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { PublicClientApplication } from '@azure/msal-browser';
import App from './App';
import { MsalBootstrap } from './auth/MsalBootstrap';
import { MSAL_ENABLED, msalConfig } from './auth/msalConfig';
import { mountSplash, showBootError } from './components/brand/splashHost';
import { applyTweaks, loadTweaks } from './shell/tweaks';
import { initAppHeightVar } from './lib/viewport';
import { initTelemetry, reportError } from './lib/telemetry';
import './styles/tokens.css';
import './styles/globals.css';

// Legacy-origin redirect: the SWA default hostname keeps serving after the
// custom domain goes live (it cannot be disabled), so bounce to
// daedalus.synconllc.com before MSAL init — no auth interaction may start
// on the legacy origin. Path/query/hash are preserved so citation deep
// links survive; a stale MSAL #code=… fragment carried across origins
// can't be redeemed (PKCE state lives in the old origin's sessionStorage)
// and degrades to a fresh sign-in prompt.
const LEGACY_HOST = 'brave-mushroom-050b4f00f.7.azurestaticapps.net';
const redirectingToCustomDomain = window.location.hostname === LEGACY_HOST;
if (redirectingToCustomDomain) {
  window.location.replace(
    `https://daedalus.synconllc.com${window.location.pathname}${window.location.search}${window.location.hash}`
  );
}

// App telemetry (lib/telemetry.ts) starts before anything else, so a failed
// start or sign-in is reported too. Off unless the build has a connection
// string. The user id is read per item, from whichever account MSAL has active.
let telemetryMsal: PublicClientApplication | null = null;
if (!redirectingToCustomDomain) {
  initTelemetry(() => {
    const account = telemetryMsal?.getActiveAccount();
    const oid = account?.idTokenClaims?.oid ?? account?.localAccountId;
    return oid ? oid.toLowerCase() : null;
  });
}

// Apply persisted user preferences (theme, accent, density, motion, chat
// width) before React mounts so the sign-in gate respects them from the
// very first paint — not just after the TweaksPanel hydrates inside AppShell.
applyTweaks(loadTweaks());
// Track the visual viewport so the shell shrinks above the mobile keyboard.
initAppHeightVar();

// iOS Safari back-swipe from the Microsoft login page restores the SPA
// from bfcache with MSAL's interaction-in-progress flag still set, which
// leaves the Sign-in button dead. Reload ONLY when that stuck marker is
// actually present — an unconditional persisted→reload would defeat
// bfcache on every platform (desktop back/forward would hard-reload and
// replay the splash instead of restoring instantly).
if (MSAL_ENABLED) {
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    try {
      // msal-browser keeps its in-flight interaction marker in web storage
      // under a key containing "interaction.status"; a live SPA has none.
      const stuck = [sessionStorage, localStorage].some((store) =>
        Object.keys(store).some((k) => k.includes('interaction.status'))
      );
      if (stuck) window.location.reload();
    } catch {
      /* storage unavailable — leave the bfcache restore alone */
    }
  });
}

async function boot() {
  // Paint the brand splash IMMEDIATELY, in its own React root, so MSW
  // startup and MSAL initialize/handleRedirectPromise below run behind it
  // instead of in front of a blank page. The app root below must still
  // mount only after handleRedirectPromise (Router <Navigate> strips the
  // #code fragment — see the comment on the MSAL block).
  mountSplash();

  // Phase 0.5 — install MSW handlers when the mock flag is on.
  // Keeps SPA routes/store identical in real + mock modes; only the
  // transport differs (MSW intercepts fetch before it hits the network).
  if (import.meta.env.VITE_USE_MOCKS === 'true') {
    const { worker } = await import('./api/mocks/browser');
    await worker.start({
      onUnhandledRequest: 'bypass',
      serviceWorker: { url: '/mockServiceWorker.js' },
    });
  }

  // MSAL initialization + redirect handling MUST happen before React (and
  // therefore before React Router) mounts. React Router's `<Navigate>` runs
  // synchronously during render and will strip the `#code=...` fragment
  // MSAL needs to read. If we wait for MsalBootstrap's useEffect to call
  // handleRedirectPromise, the fragment is already gone and sign-in silently
  // fails. Doing it here, pre-mount, guarantees MSAL sees the raw URL.
  let msal: PublicClientApplication | null = null;
  if (MSAL_ENABLED) {
    msal = new PublicClientApplication(msalConfig);
    telemetryMsal = msal;
    await msal.initialize();
    try {
      const resp = await msal.handleRedirectPromise();
      const account = resp?.account ?? msal.getAllAccounts()[0] ?? null;
      if (account) {
        msal.setActiveAccount(account);
      }
    } catch (err) {
      // Log + swallow — the app mounts anyway and the SignInGate catches
      // the no-active-account case. Swallowing here avoids a blank page
      // on transient MSAL errors (stale interaction state, etc.); the
      // user can just re-click Sign In.
      console.error('[MSAL] pre-mount redirect handling failed:', err);
      reportError(err, 'sign-in');
    }
  }

  const root = document.getElementById('root');
  if (!root) throw new Error('root container not found');
  createRoot(root).render(
    <StrictMode>
      <BrowserRouter>
        <MsalBootstrap msal={msal}>
          <App />
        </MsalBootstrap>
      </BrowserRouter>
    </StrictMode>
  );
}

if (!redirectingToCustomDomain) {
  boot().catch((err) => {
    console.error('[boot] fatal:', err);
    reportError(err, 'boot');
    showBootError(); // static "failed to start / reload" panel over the splash host
  });
}
